import { useState } from "react";
import DeeperLookIcon_Trans from "../../../assets/UI/DeeperLookIcon_Trans.png";
import HeaderNavNTM from "../../../Components/Header/Header_Nav_NoBottomMargin/Header_Nav_NBM";
import "../DeeperLook.css"; 


export default function FamilyDLook() {

const categories = {

    FamilyOverview: {
        title: "Family in Korean: Overview",
        content: [
            { type: "subheader", content: "Overview of Family Vocabulary" },
            { type: "paragraph", content: "Family is a central part of Korean culture, and learning family-related vocabulary helps you talk about your relatives, understand social relationships, and navigate conversations about family life." },
            { type: "paragraph", content: "In Korean, family words are often specific to the relative’s gender, age, or relationship to you, so it’s important to learn the proper terms to sound natural and respectful." },
            { type: "paragraph", content: "This deck will help you:" },
            { type: "list", items: [
                "Identify family members with correct Korean terms",
                "Understand honorifics and casual forms",
                "Use family vocabulary in everyday sentences"
            ] },
            { type: "paragraph", content: "💡 Tip: Korean often distinguishes between older and younger siblings, as well as between maternal and paternal relatives, so context matters." }
        ]
    },
    
    ImmediateFamilyVocabulary: {
        title: "Immediate Family Vocabulary",
        content: [
            { type: "subheader", content: "Overview of Immediate Family" },
            { type: "paragraph", content: "These are the basic words for your closest family members in Korean. Knowing these helps you introduce your family and talk about your home life." },
            { type: "subheader", content: "Common Immediate Family Terms" },
            { 
                type: "table", 
                rows: [
                    ["English", "Korean (Hangul)", "Notes"],
                    ["Father", "아버지 (abeoji)", "Formal / polite; 아빠 (appa) is informal/casual"],
                    ["Mother", "어머니 (eomeoni)", "Formal / polite; 엄마 (eomma) is informal/casual"],
                    ["Older Brother (for males)", "형 (hyeong)", "Used by males"],
                    ["Older Brother (for females)", "오빠 (oppa)", "Used by females"],
                    ["Older Sister (for males)", "누나 (nuna)", "Used by males"],
                    ["Older Sister (for females)", "언니 (eonni)", "Used by females"],
                    ["Younger Brother", "남동생 (namdongsaeng)", "Gender-neutral for younger brother"],
                    ["Younger Sister", "여동생 (yeodongsaeng)", "Gender-neutral for younger sister"],
                    ["Myself / I", "저 (jeo, formal) / 나 (na, informal)", "Use depending on formality"]
                ]
            },
            { type: "subheader", content: "Examples" },
            { type: "paragraph", content: "제 아버지는 선생님이에요. → My father is a teacher." },
            { type: "paragraph", content: "저는 여동생이 두 명 있어요. → I have two younger sisters." },
            { type: "paragraph", content: "오빠가 축구를 잘해요. → My older brother (for females) plays soccer well." },
            { type: "paragraph", content: "💡 Tip: Koreans often use titles instead of names when referring to family members directly. For example, instead of saying 'my older brother,' a female might just say '오빠' in conversation." }
        ]
    },

    ExtendedFamilyVocabulary: {
        title: "Extended Family Vocabulary",
        content: [
            { type: "subheader", content: "Overview of Extended Family" },
            { type: "paragraph", content: "These words are for relatives beyond your immediate family. In Korean, extended family terms are often more specific than in English, so learning the correct term shows respect and understanding of relationships." },
            { type: "subheader", content: "Common Extended Family Terms" },
            { 
                type: "table", 
                rows: [
                    ["English", "Korean (Hangul)", "Notes"],
                    ["Grandfather", "할아버지 (hal-abeoji)", "Formal; informal/familiar use is same"],
                    ["Grandmother", "할머니 (halmeoni)", "–"],
                    ["Uncle (father’s side)", "삼촌 (samchon)", "Casual; formal titles vary"],
                    ["Aunt (father’s side)", "고모 (gomo)", "Aunt on father’s side"],
                    ["Uncle (mother’s side)", "외삼촌 (oesamchon)", "Uncle on mother’s side"],
                    ["Aunt (mother’s side)", "이모 (imo)", "Aunt on mother’s side"],
                    ["Cousin (male/female)", "사촌 (sachon)", "Gender-neutral; can add 남/여 for clarity"]
                ]
            },
            { type: "subheader", content: "Examples" },
            { type: "paragraph", content: "할머니가 요리를 잘하세요. → My grandmother cooks well." },
            { type: "paragraph", content: "삼촌이 서울에 사세요. → My uncle lives in Seoul." },
            { type: "paragraph", content: "사촌들이 모두 모였어요. → All my cousins gathered together." },
            { type: "paragraph", content: "💡 Tip: In Korean, maternal and paternal relatives often have different words, so it’s important to use the correct one depending on the side of the family." }
        ]
    },
    
    HonorificsPoliteness: {
        title: "Honorifics and Politeness",
        content: [
            { type: "subheader", content: "Overview" },
            { type: "paragraph", content: "In Korean, using honorifics when talking about family shows respect and good manners, especially with elders. The words you choose and the way you speak depend on age, relationship, and context." },
            { type: "subheader", content: "Key Points" },
            { type: "list", items: [
                "Formal terms are used when speaking politely about your family to others: 아버지, 어머니, 할아버지, 할머니",
                "Informal or casual terms are used within your family or with close relatives: 아빠, 엄마, 할배, 할매 (less formal)",
                "When talking about someone else’s family, always use formal terms to show respect",
                "Verbs often take honorific endings when referring to elders: 하세요 / 계세요 / 주무세요"
            ] },
            { type: "subheader", content: "Examples" },
            { type: "paragraph", content: "할머니께서 주무세요. → My grandmother is sleeping. (honorific)" },
            { type: "paragraph", content: "아버지가 집에 계세요. → My father is at home." },
            { type: "paragraph", content: "엄마가 요리해요. → Mom is cooking. (informal, casual)" },
            { type: "paragraph", content: "💡 Tip: Korean emphasizes respect through both vocabulary and grammar, so learning both is important for natural conversation." }
        ]
    },

    TalkingAboutFamily: {
        title: "Talking About Your Family",
        content: [
            { type: "subheader", content: "Overview" },
            { type: "paragraph", content: "Once you know family vocabulary, you can start describing your family in sentences naturally." },
            { type: "subheader", content: "Sentence Structures" },
            { type: "paragraph", content: "Subject + Family Member + Verb" },
            { type: "paragraph", content: "제 아버지는 의사예요. → My father is a doctor." },
            { type: "paragraph", content: "언니가 한국에 살아요. → My older sister lives in Korea." },
            { type: "subheader", content: "Using Numbers to Talk About Siblings" },
            { type: "paragraph", content: "저는 형이 한 명 있어요. → I have one older brother." },
            { type: "paragraph", content: "남동생이 두 명 있어요. → I have two younger brothers." },
            { type: "subheader", content: "Using Honorifics with Family Members" },
            { type: "paragraph", content: "할머니께서 주무세요. → My grandmother is sleeping." },
            { type: "paragraph", content: "아버지께서 책을 읽으세요. → My father is reading a book." },
            { type: "subheader", content: "Talking About Multiple Family Members" },
            { type: "paragraph", content: "우리 가족은 다섯 명이에요. → There are five people in my family." },
            { type: "paragraph", content: "사촌들이 모두 모였어요. → All my cousins gathered together." },
            { type: "paragraph", content: "💡 Tip: In Korean, the subject is often omitted when context is clear. For example:\n\n아버지가 의사예요 → He is a doctor / My father is a doctor (context clarifies meaning)." }
        ]
    },

    CommonPhrasesFamily: {
        title: "Common Phrases with Family",
        content: [
            { type: "subheader", content: "Overview" },
            { type: "paragraph", content: "These everyday expressions help you talk about your family naturally in conversation." },
            { type: "subheader", content: "Common Questions" },
            { type: "paragraph", content: "가족이 몇 명이에요? → How many people are in your family?" },
            { type: "paragraph", content: "형제자매가 있어요? → Do you have siblings?" },
            { type: "paragraph", content: "부모님은 어디에 계세요? → Where are your parents?" },
            { type: "subheader", content: "Common Statements" },
            { type: "paragraph", content: "우리 가족은 친절해요. → My family is kind." },
            { type: "paragraph", content: "언니가 결혼했어요. → My older sister is married." },
            { type: "paragraph", content: "할아버지께서 건강하세요. → My grandfather is healthy." },
            { type: "subheader", content: "Examples in Conversation" },
            { type: "paragraph", content: "A: 형이 뭐 해요? → What does your older brother do?" },
            { type: "paragraph", content: "B: 그는 학생이에요. → He is a student." },
            { type: "paragraph", content: "A: 부모님은 어디에 사세요? → Where do your parents live?" },
            { type: "paragraph", content: "B: 서울에 사세요. → They live in Seoul." },
            { type: "paragraph", content: "💡 Tip: When asking about someone else’s family, always use honorifics to show respect." }
        ]
    },






 }
 
 
   //Make overveiw the default catgory selected when page loads
   const [selectedCategory, setSelectedCategory] = useState("FamilyOverview");
 
 
   return (
     <div>
       
       <HeaderNavNTM GoBack_to="/FamilyNav"/>
 
       <div className="blue-header-dlook">
         <img
           className="DeeperLookImage-dlook"
           src={DeeperLookIcon_Trans}
           alt="Deeper Look"
         />
       </div>

       <div className="panel-container-dlook">

         <div className="side-panel-dlook">
           {Object.keys(categories).map(key => (

             <p
               key={key}

               className={key === selectedCategory 
                 ? "category-active" 
                 : "category-inactive"}

               onClick={() => setSelectedCategory(key)}
             >              
               {categories[key].title}
             </p>
           ))}
         </div>
 
         {/* Text panel */}
     <div className="text-panel-dlook">
        {(categories[selectedCategory]?.content ?? []).map((item, index) => {

 
            switch (item.type) {

  
             case "subheader":
                 return <h3 key={index} className="subheader-dlook">{item.content}</h3>;
 
             case "paragraph":
                 return <p key={index}>{item.content}</p>;
 
             case "list":
                 return (
                 <ul key={index} className="list-dlook">
                     {item.items.map((bullet, i) => (
                     <li key={i}>{bullet}</li>
                     ))}
                 </ul>
                 );
 
             case "table":
                 return (
                 <table key={index} className="table-dlook">
                     <tbody>
                     {item.rows.map((row, r) => (
                         <tr key={r}>
                         {row.map((cell, c) => (
                             <td key={c}>{cell}</td>
                         ))}
                         </tr>
                     ))}
                     </tbody>
                 </table>
                 );
 
             default:
                 return null;

             }
             })}
         </div>
 
       </div>
      
       {/* End Body */}
     
     
     
     </div>
   );{/* End return */}
 } 
 
